import type * as C from "@zooi/contracts";
import { inline } from "./skill-text.ts";

// What a playbook's page derives from the playbook itself (docs/design-system.md
// §0.6, plan M8 step 8c): which zooi tools its skill text names, which inline
// names are references to show as chips, and how far a unit's window has run.

/** The zooi MCP tools a skill text may name (plugin/spec/mcp.md). */
export const MCP_TOOLS = [
  "get_context", "get_due", "get_due_summary", "get_playbook", "get_results", "list_products", "query_metrics",
  "propose_facts", "propose_plan", "judge_candidates", "claim_work_order", "report_run", "report_run_request", "submit_receipt",
  "draft_reading", "draft_campaign_reading",
] as const;
const TOOLS: ReadonlySet<string> = new Set(MCP_TOOLS);
const PROFILE_FACTS: ReadonlySet<string> = new Set(["one_liner", "problem", "audience", "not_for", "categories"]);

/** The zooi tools a skill text names in inline code, in the order it first names them. */
export function skillTools(skill: string): string[] {
  const seen = new Set<string>();
  for (const line of skill.split("\n")) for (const part of inline(line)) if (part.kind === "code" && TOOLS.has(part.text)) seen.add(part.text);
  return [...seen];
}

export type Reference = "tool" | "input" | "fact" | "metric";

/**
 * What an inline name in the skill text refers to: a zooi tool, one of the
 * playbook's inputs (`alternative` or `inputs.alternative`), a profile fact or
 * one of its metrics. Anything else stays plain inline code.
 */
export function referenceOf(code: string, playbook: Pick<C.AgentPlaybookView, "inputs" | "measurement">): Reference | null {
  if (TOOLS.has(code)) return "tool";
  const name = code.startsWith("inputs.") ? code.slice("inputs.".length) : code;
  if (playbook.inputs.some((input) => input.name === name)) return "input";
  if (PROFILE_FACTS.has(code)) return "fact";
  if (playbook.measurement.primary_metrics.includes(code)) return "metric";
  return null;
}

/** The campaigns a playbook's actions belong to, each once, in the order the actions come (newest first): where its tables are (plan M8 step 8d). */
export function campaignsOf(actions: readonly Pick<NonNullable<C.PlaybookPage["actions"]>[number], "campaign">[]): { id: string; title: string }[] {
  const seen = new Map<string, { id: string; title: string }>();
  for (const action of actions) if (action.campaign && !seen.has(action.campaign.id)) seen.set(action.campaign.id, action.campaign);
  return [...seen.values()];
}

/** The observation window of a unit that went live: which day it is on, out of how many; null before it starts or without a window. */
export function windowDay(action: Pick<NonNullable<C.PlaybookPage["actions"]>[number], "status" | "activated_at" | "window_days">, now: number): { day: number; of: number } | null {
  if (!action.window_days || !action.activated_at || !["live", "reading_due"].includes(action.status)) return null;
  const elapsed = Math.floor((now - Date.parse(action.activated_at)) / 86_400_000) + 1;
  return { day: Math.min(Math.max(elapsed, 1), action.window_days), of: action.window_days };
}

/** A pull request's number from its URL, to show instead of the whole link. */
export function deliverableLabel(url: string): string {
  const pull = /\/pull\/(\d+)(?:[/?#]|$)/.exec(url);
  return pull ? `#${pull[1]}` : url.replace(/^https?:\/\/(www\.)?/, "").slice(0, 40);
}

/** How long a run took, in whole minutes; null while it runs. */
export function runMinutes(run: Pick<NonNullable<C.PlaybookPage["runs"]>[number], "started_at" | "ended_at">): number | null {
  if (!run.ended_at) return null;
  return Math.max(1, Math.round((Date.parse(run.ended_at) - Date.parse(run.started_at)) / 60_000));
}
